import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { useI18n } from '@/i18n/useI18n';

interface ErrorBoundaryProps {
    children: ReactNode;
    /**
     * When this value changes the boundary clears its captured error. Pass
     * `useLocation().key` to auto-recover on route changes — cheaper than
     * remounting the whole subtree via `key=` (which trashes in-flight
     * query state on every navigation).
     */
    resetKey?: unknown;
}

interface ErrorBoundaryState {
    error: Error | null;
}

/**
 * Catches render-time errors thrown by descendant pages so a single broken
 * route doesn't blank the whole admin shell. Mutation/fetch errors should
 * still be surfaced via toast in the page itself — this is the last-resort
 * net for unexpected exceptions.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { error: null };

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { error };
    }

    componentDidUpdate(prevProps: ErrorBoundaryProps): void {
        if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ error: null });
        }
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        // eslint-disable-next-line no-console
        console.error('[admin] uncaught render error', error, info);
    }

    handleReset = (): void => {
        this.setState({ error: null });
    };

    render() {
        if (this.state.error) {
            return <ErrorFallback error={this.state.error} onReset={this.handleReset} />;
        }
        return this.props.children;
    }
}

function ErrorFallback({ error, onReset }: { error: Error; onReset: () => void }) {
    const { t } = useI18n();

    return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6 text-center">
            <div className="space-y-2">
                <h1 className="text-2xl font-semibold">{t('errorBoundary.title')}</h1>
                <p className="text-muted-foreground">{t('errorBoundary.description')}</p>
                <p className="text-xs text-muted-foreground font-mono">{error.message}</p>
            </div>
            <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={onReset}>
                    {t('errorBoundary.retry')}
                </Button>
                <Button size="sm" onClick={() => { window.location.href = '/'; }}>
                    {t('errorBoundary.home')}
                </Button>
            </div>
        </div>
    );
}
